import { Question, TicketQuestion } from "types"
import { sanitizeFieldName } from "./contact-field-utils"

export interface UnifiedContactFieldMapping {
  firstName?: {
    fieldName: string
    question: Question | TicketQuestion
  }
  lastName?: {
    fieldName: string
    question: Question | TicketQuestion
  }
  email?: {
    fieldName: string
    question: Question | TicketQuestion
  }
}

/**
 * Unified contact field detection that works for both basic and advanced forms
 * @param questions - Array of questions (Question[] for advanced, TicketQuestion[] for basic)
 * @param ticketId - Ticket ID (required for basic forms)
 * @param isAdvancedForm - Whether this is an advanced form
 * @returns Unified mapping with field names that work for both form types
 */
export function detectUnifiedContactFields(
  questions: Question[] | TicketQuestion[],
  ticketId: number,
  isAdvancedForm: boolean
): UnifiedContactFieldMapping | null {
  if (!questions || questions.length === 0) return null

  if (isAdvancedForm) {
    // Advanced forms: Use editable=false to identify system-created contact fields.
    // These are the only questions guaranteed to be First Name, Last Name, and Email
    // (created by the backend when a ticket type is created). User-added questions
    // always have editable=true, so rank-based positional detection is unreliable.
    const advancedQuestions = questions as Question[]
    const requiredFreeResponseQuestions = advancedQuestions
      .filter((q) => q.question_type === "free-response" && q.editable === false)
      .sort((a, b) => a.rank - b.rank)

    if (requiredFreeResponseQuestions.length < 3) {
      return null
    }

    const [firstQuestion, secondQuestion, thirdQuestion] = requiredFreeResponseQuestions

    return {
      firstName: {
        fieldName: `tickets.${ticketId}.${firstQuestion.uuid}`,
        question: firstQuestion,
      },
      lastName: {
        fieldName: `tickets.${ticketId}.${secondQuestion.uuid}`,
        question: secondQuestion,
      },
      email: {
        fieldName: `tickets.${ticketId}.${thirdQuestion.uuid}`,
        question: thirdQuestion,
      },
    }
  } else {
    // Basic forms: Use positional lookup with text validation
    const basicQuestions = questions as TicketQuestion[]

    if (basicQuestions.length < 3) {
      return null
    }

    const [firstQuestion, secondQuestion, thirdQuestion] = basicQuestions

    // Validate that questions match expected contact fields
    const firstText = firstQuestion.text.toLowerCase()
    const secondText = secondQuestion.text.toLowerCase()
    const thirdText = thirdQuestion.text.toLowerCase()

    if (
      !firstText.includes("first name") ||
      !secondText.includes("last name") ||
      !thirdText.includes("email")
    ) {
      return null // Questions don't match expected contact field pattern
    }

    return {
      firstName: {
        fieldName: `tickets.${ticketId}.${sanitizeFieldName(firstQuestion.text)}`,
        question: firstQuestion,
      },
      lastName: {
        fieldName: `tickets.${ticketId}.${sanitizeFieldName(secondQuestion.text)}`,
        question: secondQuestion,
      },
      email: {
        fieldName: `tickets.${ticketId}.${sanitizeFieldName(thirdQuestion.text)}`,
        question: thirdQuestion,
      },
    }
  }
}
